import { HttpClient } from '@angular/common/http';
import { catchError, expand, map, Observable, of, takeWhile, scan } from 'rxjs';
import { Dtos } from '#models';
import { inject, Injectable } from '@angular/core';
import { environment } from '#environments';
import { Server } from '#enums';

@Injectable({
  providedIn: 'root',
})
export class BusinessUnitService {
  private http = inject(HttpClient);

  reImportSharedUsers(
    businessUnitId: number,
    orgId: number,
    pageSize = 10
  ): Observable<Dtos.ReImportSharedUsersResultModel> {
    return of({ pageNumber: 1, results: [] as Dtos.ReImportSharedUsersResultModel[] }).pipe(
      // Recursively fetch pages until we've retrieved all users
      expand(({ pageNumber, results }) =>
        this.http
          .put<Dtos.ReImportSharedUsersResultModel>(
            `${environment.apiUrl}/business-unit/${businessUnitId}/re-import-shared-users`,
            {
              OrganizationId: orgId,
              PageNumber: pageNumber,
              PageSize: pageSize,
            }
          )
          .pipe(
            map(res => {
              const updatedResults = [...results, res];
              // Stop expanding if we've fetched all pages
              const shouldContinue = pageNumber * pageSize < res.TotalSharedUsers;
              return {
                pageNumber: pageNumber + 1,
                results: updatedResults,
                shouldStop: !shouldContinue,
              };
            }),
            catchError(error => {
              // Create error result for this page
              const errorResult: Dtos.ReImportSharedUsersResultModel = {
                Errors: [error.message || 'Unknown error'],
                ImportedSharedUsers: 0,
                NotFoundSharedUsers: 0,
                Status: Server.ReImportSharedUsersStatus.Error,
                TotalSharedUsers: results.length > 0 ? results[results.length - 1].TotalSharedUsers : 0,
              };
              return of({
                pageNumber: pageNumber + 1,
                results: [...results, errorResult],
                shouldStop: true, // Stop on error
              });
            })
          )
      ),
      // Stop when all pages are fetched
      takeWhile(({ shouldStop }) => !shouldStop, true),
      // Emit progress updates for each page using scan (instead of waiting for all pages)
      scan(
        (accumulated, { results }) => {
          const latestResult = results[results.length - 1] || {
            Errors: [],
            ImportedSharedUsers: 0,
            NotFoundSharedUsers: 0,
            Status: Server.ReImportSharedUsersStatus.None,
            TotalSharedUsers: 0,
          };
          return {
            Errors: [...accumulated.Errors, ...latestResult.Errors],
            ImportedSharedUsers: accumulated.ImportedSharedUsers + latestResult.ImportedSharedUsers,
            NotFoundSharedUsers: accumulated.NotFoundSharedUsers + latestResult.NotFoundSharedUsers,
            TotalSharedUsers: latestResult.TotalSharedUsers,
            Status: accumulated.Status,
          };
        },
        {
          Errors: [] as string[],
          ImportedSharedUsers: 0,
          NotFoundSharedUsers: 0,
          TotalSharedUsers: 0,
          Status: Server.ReImportSharedUsersStatus.Success,
        }
      ),
      // Determine final status at each emission
      map(aggregated => ({
        ...aggregated,
        Status:
          aggregated.Errors.length > 0
            ? Server.ReImportSharedUsersStatus.Error
            : Server.ReImportSharedUsersStatus.Success,
      }))
    );
  }

  public getAll(): Observable<Dtos.BusinessUnit[]> {
    return this.http.get<Dtos.BusinessUnit[]>(`${environment.apiUrl}/business-unit/all`);
  }

  public getUserAdminBUs(checkForOrganizationAdminUser = false): Observable<Dtos.BusinessUnit[]> {
    return this.http.get<Dtos.BusinessUnit[]>(`${environment.apiUrl}/business-unit/currentUserAdminBUs`, {
      params: {
        checkForOrganizationAdminUser: `${checkForOrganizationAdminUser}`,
      },
    });
  }

  public getUserBUs() {
    return this.http.get<Dtos.BusinessUnit[]>(`${environment.apiUrl}/business-unit/currentUserBUs`);
  }

  public getBusinessUnitById(businessUnitId: number) {
    return this.http.get<Dtos.BusinessUnit>(`${environment.apiUrl}/business-unit/${businessUnitId}`);
  }

  public addBusinessUnit(businessUnit: Dtos.BusinessUnit): Observable<Dtos.BusinessUnit> {
    return this.http.post<Dtos.BusinessUnit>(`${environment.apiUrl}/business-unit`, businessUnit);
  }

  public updateBusinessUnit(businessUnit: Dtos.BusinessUnit): Observable<Dtos.BusinessUnit> {
    return this.http.put<Dtos.BusinessUnit>(`${environment.apiUrl}/business-unit`, businessUnit);
  }

  public getBusinessUnitUsers(businessUnitId: number): Observable<Dtos.BusinessUnitUser[]> {
    return this.http.get<Dtos.BusinessUnitUser[]>(
      `${environment.apiUrl}/business-unit/businessUnitUsers/${businessUnitId}`
    );
  }

  public addBusinessUnitUser(buUser: Dtos.BusinessUnitUser): Observable<Dtos.BusinessUnitUser> {
    return this.http.post<Dtos.BusinessUnitUser>(`${environment.apiUrl}/business-unit/addBusinessUnitUser`, buUser);
  }

  public removeBusinessUnitUser(id: number): Observable<Dtos.BusinessUnitUser> {
    return this.http.put<Dtos.BusinessUnitUser>(
      `${environment.apiUrl}/business-unit/removeBusinessUnitUser/${id}`,
      null
    );
  }

  public allowMembersToCompletePSSR(bu: Dtos.BusinessUnit, pssr: Dtos.PSSR): boolean | null {
    if (
      bu &&
      bu.AllowMembersToCompletePSSR &&
      (pssr.Status === Server.PSSRStatus.InProgress || pssr.Status === Server.PSSRStatus.Initiated)
    ) {
      return (
        pssr.PSSRUsers.Users.some(u => u.UserID === pssr.CurrentUser.UserID) ||
        pssr.CurrentUser.Role !== Server.UserRole.None
      );
    }
    return null;
  }
}
